// src/utils/assetsManifest.js
// Pure Hosting version (no Firebase SDK). Serve manifest + assets via Firebase Hosting.
//
// Expected manifest shape (supports BOTH styles):
// {
//   "basePrefix": "assets/interactive-stories/",   // optional but recommended
//   "version": 1739462340000,
//   // EITHER relative entries (legacy):
//   //   "backgrounds": ["backgrounds/bg-001.webp", ...],
//   // OR already-prefixed absolute-from-project-root:
//   //   "backgrounds": ["assets/interactive-stories/backgrounds/bg-001.webp", ...],
//   "characters": {
//     "female": { "template": [...], "hair": [...], "eyes": [...], "lips": [...], "nose": [...], "top": [...], "bottom": [...], "fullbody": [...] },
//     "male":   { "template": [...], "hair": [...], "eyes": [...], "lips": [...], "nose": [...], "top": [...], "bottom": [...], "fullbody": [...] }
//   }
// }

let _manifest = null;
let _source = "hosting"; // "hosting" | "storage"
const DEFAULT_URL = "/assets/interactive-stories/assets_manifest.json";
let _loadPromise = null;

export function setAssetsSource(source) {
  _source = source === "storage" ? "storage" : "hosting";
}

export async function loadAssetsManifest({ url = DEFAULT_URL } = {}) {
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) throw new Error(`Failed to fetch manifest: HTTP ${res.status}`);

  // Read as text first; protect against HTML/SPA fallback
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  const raw = await res.text();
  if (!ct.includes("application/json")) {
    if (/^\s*<!doctype\s+html/i.test(raw) || /^\s*<html[\s>]/i.test(raw)) {
      throw new Error("Assets manifest URL returned HTML (likely rewrite/404). Check hosting path & rewrites.");
    }
  }
  try {
    _manifest = JSON.parse(raw);
  } catch (e) {
    throw new Error(`Assets manifest is not valid JSON: ${e?.message || e}`);
  }
  return _manifest;
}

export function getManifest() {
  if (!_manifest) throw new Error("Manifest not loaded. Call ensureAssetsManifest() first.");
  return _manifest;
}

export async function ensureAssetsManifest({ url = DEFAULT_URL } = {}) {
  if (_manifest) return _manifest;
  if (_loadPromise) return _loadPromise;
  _loadPromise = loadAssetsManifest({ url })
    .catch((e) => { throw e; })
    .finally(() => { _loadPromise = null; });
  return _loadPromise;
}

export function isAssetsManifestLoaded() {
  return !!_manifest;
}

function normalizeBasePrefix(manifest) {
  const base = String(manifest?.basePrefix || "assets/interactive-stories")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
  return base;
}

function withBasePrefix(rel) {
  const m = getManifest();
  const raw = String(rel || "");
  if (!raw) return "";
  // absolute URL? leave as-is
  if (/^https?:\/\//i.test(raw)) return raw;

  const base = normalizeBasePrefix(m);
  // strip leading slash from rel
  const r = raw.replace(/^\/+/, "");
  // if rel already starts with the base prefix, don't prepend again
  if (r.startsWith(base + "/") || r === base) {
    return r;
  }
  // otherwise, treat as relative to base ("backgrounds/...", "characters/...", etc.)
  return `${base}/${r}`;
}

function toHostingUrl(relOrAbs) {
  if (!relOrAbs) return "";
  if (/^https?:\/\//i.test(relOrAbs)) return relOrAbs;
  return relOrAbs.startsWith("/") ? relOrAbs : "/" + relOrAbs;
}

// (optional) storage fallback
let _storage, _getDownloadURL, _ref, _getStorage;
async function ensureStorage() {
  if (_storage) return;
  const mod = await import("firebase/storage");
  _getStorage = mod.getStorage;
  _getDownloadURL = mod.getDownloadURL;
  _ref = mod.ref;
  _storage = _getStorage();
}
async function toStorageUrl(rel) {
  await ensureStorage();
  if (!rel) return "";
  // already absolute or gs://?
  if (/^https?:\/\//i.test(rel) || /^gs:\/\//i.test(rel)) return rel;
  const path = rel.startsWith("/") ? rel.slice(1) : rel;
  const r = _ref(_storage, path);
  return await _getDownloadURL(r);
}

// Public resolvers (lazy-load manifest on-demand)
export async function resolveAssetUrl(relPath) {
  await ensureAssetsManifest();
  if (!relPath) return "";
  const abs = withBasePrefix(relPath);
  if (_source === "hosting") return toHostingUrl(abs);
  return await toStorageUrl(abs);
}

export async function resolveBackgroundUrlByIndex(idx) {
  await ensureAssetsManifest();
  const list = getManifest().backgrounds || [];
  const i = Math.max(1, Number(idx || 1)) - 1;
  const rel = list[i];
  if (!rel) return "";
  return await resolveAssetUrl(rel);
}

export async function resolveCharacterPartUrl({ gender = "female", part = "hair", index = 1 }) {
  await ensureAssetsManifest();
  const m = getManifest();
  const rel = m?.characters?.[gender]?.[part]?.[Math.max(1, Number(index || 1)) - 1];
  if (!rel) return "";
  return await resolveAssetUrl(rel);
}

export function listAllManifestPaths() {
  const m = getManifest();
  const out = [];
  for (const rel of (m.backgrounds || [])) out.push(rel);
  const chars = m.characters || {};
  for (const gender of Object.keys(chars)) {
    const partsMap = chars[gender] || {};
    for (const part of Object.keys(partsMap)) {
      for (const rel of (partsMap[part] || [])) out.push(rel);
    }
  }
  return out;
}

// Optional helper for components that still build URLs directly from manifest entries
export function asPublicUrl(relOrAbs) {
  if (!relOrAbs) return "";
  if (/^https?:\/\//i.test(relOrAbs)) return relOrAbs;
  return toHostingUrl(withBasePrefix(relOrAbs));
}
